import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import TechStack from '../components/TechStack';
import './Services.css';

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const groups = [
    {
      id: 'web',
      eyebrow: 'Web applications',
      title: 'Microsoft .NET Platforms',
      intro:
        'Softnova has delivered .NET systems for operations teams, customer portals, and regulated workflows. We work in C# and ASP.NET Core the way enterprise teams expect: clear domains, automated tests, observable production environments, and documentation another senior engineer can take over. Our experience covers greenfield products and the harder work of modernizing Framework-era applications onto .NET 8/9 without freezing the business.',
      items: [
        {
          id: 'dotnet-apps',
          title: 'Custom .NET Web Applications',
          description:
            'We design and build business applications around real operating models, multi-branch companies, partner networks, internal shared services, and customer self-service. Typical engagements include role-based portals, workflow and approval engines, document-heavy back offices, and reporting surfaces that replace spreadsheet chaos. We model bounded contexts explicitly, keep UI thin, and use ASP.NET Identity or external IdPs so access control is not an afterthought. Clients come to us when a previous vendor left an unmaintainable MVC app, or when a successful pilot needs to become a multi-tenant product. We leave behind coding standards, solution structure, and a backlog the in-house team can continue.',
          technologies: [
            '.NET 8 / 9', 'C#', 'ASP.NET Core MVC', 'Razor Pages', 'Blazor Server', 'Blazor WebAssembly',
            'ASP.NET Identity', 'FluentValidation', 'AutoMapper', 'xUnit / NUnit', 'Serilog',
          ],
          features: [
            'Domain-driven solution structure and clean architecture',
            'Multi-tenant and multi-branch access models',
            'Workflow, document, and reporting modules',
            'Legacy .NET Framework to modern .NET upgrades',
            'Responsive admin and customer portals',
            'Automated tests, structured logging, and runbooks',
          ],
        },
        {
          id: 'microservices',
          title: 'Microservices, APIs & Integration',
          description:
            'Not every product needs microservices, and that judgment is part of our value. When scale, independent release cadence, or multiple client apps demand it, we split systems along business capabilities rather than technical layers. We have implemented API gateways, asynchronous workflows, and saga-style consistency for order, billing, notification, and inventory domains. Front ends, mobile apps, and partners consume versioned REST or gRPC contracts protected with OAuth 2.0 / OpenID Connect. Messaging via RabbitMQ or Azure Service Bus keeps long-running work off the request path. Observability is designed in: correlation IDs, health endpoints, OpenTelemetry traces, and dashboards operations can actually use. Where a modular monolith is the honest first step, we say so and draw the seams so you can extract services later without a rewrite.',
          technologies: [
            'ASP.NET Core Web API', 'Minimal APIs', 'gRPC', 'YARP', 'Ocelot', 'MassTransit',
            'RabbitMQ', 'Azure Service Bus', 'Apache Kafka', 'Redis', 'Polly', 'MediatR',
            'CQRS', 'OpenTelemetry', 'Consul', 'JWT / OAuth 2.0 / OIDC', 'Hangfire / Quartz',
          ],
          features: [
            'Service boundaries mapped to business capabilities',
            'API gateways, BFF patterns, and versioned contracts',
            'Event-driven workflows and outbox/inbox reliability',
            'Resilience: retries, circuit breakers, timeouts',
            'OpenAPI docs, contract tests, and consumer-driven checks',
            'Distributed tracing, metrics, and alerting',
          ],
        },
        {
          id: 'data-cloud',
          title: 'Data, Cloud & DevOps on Azure',
          description:
            'A well-written application still fails if releases are manual and data is opaque. We implement EF Core models that survive change, SQL Server / Azure SQL tuning for real query patterns, and caching where it pays off. On Azure we host App Services, Functions, Container Apps, and AKS depending on the operating model. CI/CD through Azure DevOps or GitHub Actions promotes the same artifact from staging to production. Secrets stay out of source, migrations are versioned, and Application Insights plus health checks tell you what broke before customers do. We regularly inherit environments with no staging slot and no backup drill, and leave a promotion path your team can run on a Friday without fear.',
          technologies: [
            'Entity Framework Core', 'Dapper', 'SQL Server / Azure SQL', 'Cosmos DB', 'Redis Cache',
            'Azure App Service', 'Azure Functions', 'Azure Container Apps', 'AKS', 'Docker',
            'Kubernetes', 'Bicep / ARM', 'Azure DevOps', 'GitHub Actions', 'Application Insights',
          ],
          features: [
            'Relational modeling, indexing, and query performance',
            'Containerized services and environment parity',
            'Infrastructure as code and secret management',
            'Blue/green or slot swaps for low-downtime releases',
            'Backup, restore, and disaster-recovery rehearsals',
            'Handover so your operations team owns the pipeline',
          ],
        },
      ],
    },
    {
      id: 'php',
      eyebrow: 'PHP engineering',
      title: 'PHP, Laravel & High-Traffic Web',
      intro:
        'PHP remains one of the most practical stacks for content platforms, operational portals, and commerce. Softnova builds on PHP 8 and Laravel when speed-to-value and a large hiring market matter, and we also rescue older procedural or early-Laravel codebases. Our PHP work sits alongside .NET when a client already has mixed estates, we integrate rather than force a single religion.',
      items: [
        {
          id: 'laravel',
          title: 'Laravel & Modern PHP Applications',
          description:
            'We deliver Laravel applications with the same engineering discipline we apply to .NET: queued jobs, policy-based authorization, Form Requests, and test coverage on the paths that move money or data. Experience includes multi-role portals, booking and marketplace flows, membership products, and admin consoles that non-technical staff can actually use. We use Eloquent carefully (N+1 is a production incident, not a style debate), Redis for cache and queues, and Horizon or equivalent for visibility. For teams coming from WordPress or custom PHP 5/7, we plan a strangler migration so the public site keeps running while modules move to Laravel. APIs are first-class, Sanctum or Passport for token auth, so the same backend can serve a future mobile app.',
          technologies: [
            'PHP 8.2 / 8.3', 'Laravel 10 / 11', 'Livewire', 'Inertia', 'Eloquent ORM',
            'MySQL / MariaDB', 'PostgreSQL', 'Redis', 'Queues / Horizon', 'PHPUnit / Pest',
            'Composer', 'Nginx', 'Laravel Sanctum / Passport',
          ],
          features: [
            'Greenfield Laravel platforms and module design',
            'Legacy PHP modernization without a big-bang cutover',
            'Authenticated APIs for web and mobile clients',
            'Queue-backed email, imports, and report generation',
            'Role, permission, and audit patterns',
            'Performance profiling and query hygiene',
          ],
        },
        {
          id: 'php-integrations',
          title: 'PHP Integrations, CMS & Commerce',
          description:
            'Many PHP programs live or die on integrations: payments, shipping, CRMs, ERPs, and marketing tools. We have connected Laravel and Symfony apps to Stripe and regional gateways, inventory feeds, and identity providers. Where a CMS is the right surface for marketing teams, we implement headless or hybrid WordPress/Laravel setups with a clear split between editorial content and transactional logic, so a plugin update cannot take down checkout. Symfony is in the toolkit when long-lived, strictly typed components are a better fit than Laravel conventions. We document every external contract and add monitoring on webhook failures, because that is where silent revenue loss happens.',
          technologies: [
            'Symfony', 'WordPress (headless / hybrid)', 'WooCommerce APIs', 'REST & GraphQL',
            'Stripe / payment gateways', 'Elasticsearch / Meilisearch', 'Docker', 'CI pipelines',
          ],
          features: [
            'Payment, CRM, ERP, and shipping connectors',
            'Headless CMS with a Laravel or .NET transactional core',
            'Search, catalog, and inventory synchronization',
            'Webhook reliability and idempotent handlers',
            'Staging content workflows for marketing teams',
            'Security hardening and dependency audits',
          ],
        },
      ],
    },
    {
      id: 'mobile',
      eyebrow: 'Mobile applications',
      title: 'iOS & Android App Development',
      intro:
        'We ship store-ready mobile products using the latest languages and UI toolkits for each platform. Engagements range from a first MVP that must pass App Review on the first try, to replacing hybrid apps that never felt native. When a shared codebase is smarter, we use .NET MAUI, Flutter, or React Native, without pretending those choices are free of platform work.',
      items: [
        {
          id: 'ios',
          title: 'Native iOS Development',
          description:
            'Swift and SwiftUI applications built for iPhone and iPad, with UIKit where a mature control or custom transition still wins. We follow Human Interface Guidelines, plan for Dynamic Type and accessibility, and treat TestFlight as part of the delivery rhythm rather than a last-minute upload. Experience includes offline-first field apps, subscription products, and customer apps that sit on top of .NET or PHP APIs we also maintain. We handle Sign in with Apple, Keychain, push, deep links, and the privacy nutrition labels that stall unprepared submissions. After launch we stay for OS upgrades, because a store listing that dies on iOS 18 is a product failure, not a surprise.',
          technologies: ['Swift', 'SwiftUI', 'UIKit', 'Combine', 'Core Data', 'SwiftData', 'Xcode', 'TestFlight', 'StoreKit 2'],
          features: [
            'SwiftUI-first interfaces with UIKit interoperability',
            'Sign in with Apple, Face ID, and Keychain',
            'Push notifications, universal links, and widgets',
            'Offline-first data and background refresh',
            'In-app purchases and subscriptions',
            'App Store listing, review responses, and crash triage',
          ],
        },
        {
          id: 'android',
          title: 'Native Android Development',
          description:
            'Kotlin applications with Jetpack Compose and Material 3, targeted at current SDKs and a realistic device matrix. We have shipped apps used on mid-range Android hardware in the field, which forces honest performance work: startup time, image loading, and battery-friendly background jobs with WorkManager. Play Console tracks, Play Billing, and policy compliance are part of the project plan, not a week-18 discovery. When the same product exists on iOS, we align navigation and analytics events so product managers compare apples to apples.',
          technologies: ['Kotlin', 'Jetpack Compose', 'Android Jetpack', 'Coroutines / Flow', 'Room', 'Hilt', 'Android Studio', 'Play Console'],
          features: [
            'Compose UI with Material 3 and adaptive layouts',
            'Google Sign-In, biometrics, and Credential Manager',
            'FCM push notifications and app links',
            'Background work with WorkManager',
            'Play Billing and staged rollouts',
            'OEM and foldable-aware QA',
          ],
        },
        {
          id: 'cross-platform',
          title: 'Cross-Platform Apps',
          description:
            'One product vision, two stores. We recommend .NET MAUI when your backend and team already live in C#, Flutter when highly custom UI and a single rendering engine matter, and React Native when the organization is invested in JavaScript. We have seen cross-platform projects fail when native modules, store guidelines, or performance were treated as “later.” Our approach is explicit: shared business logic, native modules where the OS demands it, and a CI pipeline that produces both IPA and AAB on every release candidate. Firebase or App Center covers crash reporting and remote config so you are not blind after launch.',
          technologies: ['.NET MAUI', 'Flutter / Dart', 'React Native', 'Firebase', 'App Center', 'Fastlane', 'CodePush (where appropriate)'],
          features: [
            'Shared domain logic with native UI where it matters',
            'Single CI pipeline for iOS and Android artifacts',
            'Crash reporting and remote configuration',
            'Maps, camera, payments, and device sensors',
            'Tablet and foldable-aware layouts',
            'OS upgrade retainers after launch',
          ],
        },
      ],
    },
    {
      id: 'digital',
      eyebrow: 'Digital growth',
      title: 'Digital Marketing, SEO & Lifecycle',
      intro:
        'Engineering gets the product into the world. Digital services make sure the right people find it. We run practical, measurable programs, search, advertising, email, and content, aligned to product KPIs. Because we also build the applications, tracking is implemented correctly in the codebase rather than taped on with a tag that breaks every release.',
      items: [
        {
          id: 'seo',
          title: 'SEO Optimization',
          description:
            'Technical and content SEO for marketing sites and for application front ends that search engines struggle with, Blazor, SPAs, and Laravel-rendered catalogs included. We start with crawlability, indexation, and Core Web Vitals, then build topic clusters around the features you actually sell. Experience includes local SEO for multi-location businesses and international hreflang setups. We work in Search Console and ship schema markup developers will not rip out in the next sprint.',
          technologies: ['Technical SEO', 'On-page SEO', 'Schema markup', 'Core Web Vitals', 'Google Search Console', 'Local SEO'],
          features: [
            'Audits of .NET, PHP, and SPA sites',
            'Keyword research mapped to product features',
            'Information architecture and internal linking',
            'Content briefs and landing page recommendations',
            'Local and international SEO where relevant',
            'Monthly ranking and opportunity reports',
          ],
        },
        {
          id: 'digital-marketing',
          title: 'Digital Marketing Strategy',
          description:
            'A coherent plan across channels so ads, content, and product messaging tell the same story. Ideal when you are launching an app or entering a new market. We sit with founders and product leads to define positioning, then pick channels that match sales cycle and budget, not a generic “full funnel” slide. Campaign calendars follow your release train so a feature launch has landing pages and creative ready, not a week late.',
          technologies: ['Go-to-market planning', 'Funnel design', 'Audience research', 'Creative direction', 'Attribution'],
          features: [
            'Positioning and messaging workshops',
            'Channel mix based on budget and sales cycle',
            'Landing page and conversion path design',
            'Campaign calendars tied to product releases',
            'UTM and conversion tracking setup',
            'Quarterly strategy reviews',
          ],
        },
        {
          id: 'google-ads',
          title: 'Google Ads',
          description:
            'Search, Performance Max, Display, and YouTube campaigns that capture high-intent demand for software and services. We structure accounts so wasted spend is visible, negatives are maintained, and landing pages match query intent. Conversion tracking is implemented with your engineering team, especially important on .NET and Laravel apps where SPA routes break naive tags. Reporting is plain language: cost, qualified leads or trials, and what we will change next.',
          technologies: ['Google Ads', 'Keyword strategy', 'Conversion tracking', 'GA4', 'Landing page CRO'],
          features: [
            'Account structure and negative keyword hygiene',
            'Ad copy and asset testing',
            'Remarketing to product trial and demo audiences',
            'Budget pacing and bid strategy',
            'Lead quality feedback loops with sales',
            'Transparent performance reporting',
          ],
        },
        {
          id: 'meta-ads',
          title: 'Meta Ads (Facebook & Instagram)',
          description:
            'Awareness and acquisition campaigns for consumer and B2B apps, especially around mobile launches. We build audiences from first-party data, test creative in feed and stories, and install Pixel/CAPI properly so iOS privacy changes do not silently kill learning. App-install campaigns are coordinated with store listings and onboarding so paid traffic is not wasted on a confusing first session.',
          technologies: ['Meta Ads Manager', 'Advantage+', 'Pixel / CAPI', 'Creative testing', 'Lookalike audiences'],
          features: [
            'Audience definition from first-party data',
            'Creative concepts for feed, stories, and reels',
            'App install and lead generation campaigns',
            'Retargeting of site and app visitors',
            'A/B tests on creative and offers',
            'Weekly optimization notes',
          ],
        },
        {
          id: 'email',
          title: 'Email Marketing',
          description:
            'Lifecycle email that onboards users, nurtures leads, and keeps customers active. We design journeys for trial-to-paid software, appointment businesses, and content products. Deliverability, consent, and list hygiene are treated as engineering problems: authenticated domains, preference centers, and suppression that actually suppresses. Templates match the brand system used on the web and mobile apps so the company does not look like three vendors.',
          technologies: ['Automation journeys', 'Segmentation', 'Deliverability', 'A/B testing', 'ESP setup'],
          features: [
            'Welcome, trial, and activation sequences',
            'Newsletter and product-update programs',
            'Abandoned demo or cart flows',
            'Template systems that match your brand',
            'List hygiene and compliance (consent, unsubscribe)',
            'Revenue and engagement reporting',
          ],
        },
        {
          id: 'content-analytics',
          title: 'Content Marketing & Analytics',
          description:
            'Useful content that supports SEO and sales, plus measurement so you know what moves pipeline. We implement GA4 and Tag Manager against real product events, sign-up started, trial activated, install completed, not only pageviews. Dashboards are built for leadership attention spans. Experiment backlogs keep CRO from becoming random button-color debates.',
          technologies: ['Editorial strategy', 'GA4', 'Google Tag Manager', 'Looker Studio', 'Conversion events'],
          features: [
            'Topic clusters around your product categories',
            'Case studies and thought-leadership outlines',
            'Event tracking for sign-ups, trials, and installs',
            'Dashboards leadership can read in minutes',
            'Experiment backlog for conversion rate',
            'Handover so your team can continue publishing',
          ],
        },
      ],
    },
  ];

  return (
    <main className="services-page">
      <section className="services-hero page-hero section">
        <div className="container">
          <p className="eyebrow">Our Services</p>
          <h1 className="section-title">Engineering Depth. Digital Reach.</h1>
          <p className="section-subtitle">
            .NET and PHP web platforms, microservices, iOS and Android apps, and growth
            programs informed by years of shipping, not slideware. Scopes are agreed per
            project; we do not publish rate cards.
          </p>
          <div className="service-jump">
            <a href="#web" className="jump-link">.NET Web</a>
            <a href="#microservices" className="jump-link">Microservices</a>
            <a href="#php" className="jump-link">PHP & Laravel</a>
            <a href="#mobile" className="jump-link">iOS & Android</a>
            <a href="#digital" className="jump-link">Digital Marketing</a>
          </div>
        </div>
      </section>

      <TechStack />

      <section className="services-list section">
        <div className="container">
          {groups.map((group) => (
            <div key={group.id} id={group.id} className="service-group">
              <div className="service-group-header">
                <p className="eyebrow">{group.eyebrow}</p>
                <h2>{group.title}</h2>
                <p>{group.intro}</p>
              </div>

              {group.items.map((service) => (
                <article key={service.id} id={service.id} className="service-detail">
                  <div className="service-detail-content">
                    <h3 className="service-detail-title">{service.title}</h3>
                    <p className="service-detail-description">{service.description}</p>

                    <div className="service-tech-block">
                      <h4>Technologies we use</h4>
                      <div className="tech-tags">
                        {service.technologies.map((tech) => (
                          <span key={tech} className="tech-tag">{tech}</span>
                        ))}
                      </div>
                    </div>

                    <div className="service-features">
                      <h4 className="features-title">What we deliver</h4>
                      <ul className="features-list">
                        {service.features.map((feature) => (
                          <li key={feature}>
                            <span className="feature-check">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="services-cta section">
        <div className="container">
          <div className="services-cta-content">
            <h2 className="services-cta-title">Tell Us What You Need to Ship</h2>
            <p className="services-cta-subtitle">
              Share your timeline, platforms, and success criteria. We will come back with
              an approach grounded in similar work we have already delivered.
            </p>
            <div className="services-cta-buttons">
              <Link to="/contact" className="btn btn-light">
                Contact Us
              </Link>
              <Link to="/about" className="btn btn-secondary services-cta-secondary">
                About Softnova
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Services;
